import ApiClient from '../../core/ApiClient';

/**
 * Vuex module for the public vignette validity-check.
 *
 * Drives ValidityCheck.vue: dispatches POST /orders/check-exist via ApiClient,
 * stores the response so the form/verifying/active/not-found views can read it,
 * and exposes the field-error map for inline form validation.
 *
 *   Request body: { plate_country: <ISO-2>, license_plate: <PLATE> }
 *   Active:        200 + data: { start_date, end_date }
 *   Not-found:     200 + data: null
 *   Field errors:  422 + errors: { plate_country|license_plate: [...] }
 */

export const VALIDITY_CHECK_PATH = '/orders/check-exist';

const STATUS_IDLE = 'idle';
const STATUS_VERIFYING = 'verifying';
const STATUS_ACTIVE = 'active';
const STATUS_NOT_FOUND = 'not-found';
const STATUS_ERROR = 'error';

const defaultErrors = () => ({ country: false, plate: false });

const defaultState = () => ({
    status: STATUS_IDLE,
    /** Last result.data: { start_date, end_date } when active, null otherwise. */
    result: null,
    /** Last submitted payload echoed back so the active/not-found views can render the searched plate. */
    lastRequest: null,
    /** ISO timestamp written every time the API returns (active or not-found). */
    verifiedAt: null,
    /** Inline form errors after a 422 response (or empty-field client guard). */
    errors: defaultErrors(),
});

function normalizeApiErrorKey(key) {
    const s = String(key || '').trim();
    if (s.startsWith('form.main.')) return s.replace('form.main.', '');
    if (s.startsWith('main.')) return s.replace('main.', '');
    if (s.startsWith('form.')) return s.replace('form.', '');
    return s;
}

function applyApiErrorMap(rawErrors) {
    const errors = defaultErrors();
    if (!rawErrors || typeof rawErrors !== 'object') return errors;
    Object.keys(rawErrors).forEach((raw) => {
        const k = normalizeApiErrorKey(raw);
        if (k === 'license_plate') errors.plate = true;
        if (k === 'plate_country' || k === 'country_id' || k === 'vehicle_registration_country') {
            errors.country = true;
        }
    });
    return errors;
}

function isActiveResponse(payload) {
    const data = payload?.data;
    if (!data || typeof data !== 'object') return false;
    return Boolean(data.start_date || data.end_date);
}

export const validityCheckModule = {
    namespaced: true,

    state: defaultState(),

    mutations: {
        SET_STATUS(state, status) {
            state.status = status || STATUS_IDLE;
        },
        SET_RESULT(state, result) {
            state.result = result && typeof result === 'object' ? { ...result } : null;
        },
        SET_LAST_REQUEST(state, payload) {
            state.lastRequest = payload && typeof payload === 'object' ? { ...payload } : null;
        },
        SET_VERIFIED_AT(state, iso) {
            state.verifiedAt = iso || null;
        },
        SET_ERRORS(state, errors) {
            state.errors = {
                country: Boolean(errors?.country),
                plate: Boolean(errors?.plate),
            };
        },
        CLEAR_ERROR_FIELD(state, field) {
            if (!field) return;
            if (field === 'country' || field === 'plate') {
                state.errors = { ...state.errors, [field]: false };
            }
        },
        RESET(state) {
            const fresh = defaultState();
            state.status = fresh.status;
            state.result = fresh.result;
            state.lastRequest = fresh.lastRequest;
            state.verifiedAt = fresh.verifiedAt;
            state.errors = fresh.errors;
        },
    },

    actions: {
        reset({ commit }) {
            commit('RESET');
        },

        clearFieldError({ commit }, field) {
            commit('CLEAR_ERROR_FIELD', field);
        },

        /**
         * Silently flip into the not-found UI without hitting the API.
         * Used by the honeypot path so trivially-scripted bots see the same
         * "no active vignette" screen a real miss produces, rather than red borders.
         */
        markNotFound({ commit }, lastRequest = null) {
            commit('SET_ERRORS', defaultErrors());
            if (lastRequest && typeof lastRequest === 'object') {
                commit('SET_LAST_REQUEST', lastRequest);
            }
            commit('SET_RESULT', null);
            commit('SET_VERIFIED_AT', new Date().toISOString());
            commit('SET_STATUS', STATUS_NOT_FOUND);
        },

        /**
         * Run the check. Returns a plain result object (no throws on the not-found
         * or 422 paths so callers can handle UI states without try/catch noise).
         *
         * @returns {Promise<{ kind: 'active'|'not-found'|'errors'|'idle', data?: any, errors?: any }>}
         */
        async submitCheck({ commit }, { plate_country, license_plate } = {}) {
            const country = String(plate_country || '').trim().toUpperCase();
            const plate = String(license_plate || '').trim();

            const errors = defaultErrors();
            if (!country) errors.country = true;
            if (!plate) errors.plate = true;
            if (errors.country || errors.plate) {
                commit('SET_ERRORS', errors);
                commit('SET_STATUS', STATUS_IDLE);
                return { kind: 'idle', errors };
            }

            commit('SET_ERRORS', defaultErrors());
            commit('SET_LAST_REQUEST', { plate_country: country, license_plate: plate });
            commit('SET_STATUS', STATUS_VERIFYING);

            try {
                const res = await new ApiClient().request({
                    method: 'POST',
                    path: VALIDITY_CHECK_PATH,
                    data: { plate_country: country, license_plate: plate },
                });
                const payload = res?.data ?? {};

                commit('SET_VERIFIED_AT', new Date().toISOString());

                if (isActiveResponse(payload)) {
                    commit('SET_RESULT', payload.data);
                    commit('SET_STATUS', STATUS_ACTIVE);
                    return { kind: 'active', data: payload.data };
                }

                commit('SET_RESULT', null);
                commit('SET_STATUS', STATUS_NOT_FOUND);
                return { kind: 'not-found' };
            } catch (e) {
                const data = e?.response?.data;
                const statusCode = e?.response?.status;

                if (statusCode === 422 && data?.errors && typeof data.errors === 'object') {
                    const mapped = applyApiErrorMap(data.errors);
                    commit('SET_ERRORS', mapped);
                    commit('SET_STATUS', STATUS_IDLE);
                    return { kind: 'errors', errors: mapped };
                }

                // Network or 5xx: surface as "not-found" UI (existing behaviour) but expose status=error
                // through the dedicated state so callers may render a soft retry banner if they want.
                commit('SET_RESULT', null);
                commit('SET_VERIFIED_AT', new Date().toISOString());
                commit('SET_STATUS', STATUS_ERROR);
                return { kind: 'not-found', error: e };
            }
        },
    },

    getters: {
        status: (state) => state.status,
        result: (state) => state.result,
        lastRequest: (state) => state.lastRequest,
        verifiedAt: (state) => state.verifiedAt,
        errors: (state) => state.errors,
        isActive: (state) => state.status === STATUS_ACTIVE,
        isVerifying: (state) => state.status === STATUS_VERIFYING,
        isNotFound: (state) => state.status === STATUS_NOT_FOUND || state.status === STATUS_ERROR,
    },
};

export const ValidityCheckStatus = {
    IDLE: STATUS_IDLE,
    VERIFYING: STATUS_VERIFYING,
    ACTIVE: STATUS_ACTIVE,
    NOT_FOUND: STATUS_NOT_FOUND,
    ERROR: STATUS_ERROR,
};
